import React from 'react';
import Link from 'next/link';
import ReactDOMServer from 'react-dom/server';
import clsx from 'clsx';

import styles from './styles.module.scss';

import {
  MapCoordinates,
  MarkerIcon,
  MarkerLabel,
  MarkerType,
} from '@utils/types/maps';
import { splitByCommas } from '@utils/helpers';
import { FeaturedListingType } from '@utils/types/featuredListing';
import { DatesRange } from '@utils/types/calendar';
import { toDollars } from '@utils/money';
import { Cross } from '@assets/icons';
import ImageWithLoader from '@components/_shared/imageWithLoader';
import { colorsByTheme } from '@utils/constants/colors';
import { calculateMonthlyPrice } from '@utils/pricing';

const rad = (x: number) => {
  return (x * Math.PI) / 180;
};

const getDistance = (p1: MapCoordinates, p2: MapCoordinates) => {
  const R = 6378137; // Earth’s mean radius in meter
  const dLat = rad(p2.lat - p1.lat);
  const dLong = rad(p2.lng - p1.lng);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(rad(p1.lat)) *
      Math.cos(rad(p2.lat)) *
      Math.sin(dLong / 2) *
      Math.sin(dLong / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // returns the distance in meter
};

// Will calculate the distance between a marker point
// and map bounds. Will return an offset, so infoWindow
// will be shown on the appropriate side of the marker
// (to the side where the distance between a marker
// and map bounds is the biggest)
const getInfoWindowPosition = (
  markerPosition: MarkerType['position'],
  map?: google.maps.Map
) => {
  const windowOnTop = new google.maps.Size(0, 0);
  const windowOnRight = new google.maps.Size(200, 120);
  const windowOnBottom = new google.maps.Size(0, 240);
  const windowOnLeft = new google.maps.Size(-200, 120);
  const windowOnTopRight = new google.maps.Size(130, 0);
  const windowOnTopLeft = new google.maps.Size(-130, 0);
  const windowOnBottomRight = new google.maps.Size(130, 240);
  const windowOnBottomLeft = new google.maps.Size(-130, 240);

  if (!map) return windowOnTop;

  const aNorth = map.getBounds()?.getNorthEast().lat() || 0;
  const aEast = map.getBounds()?.getNorthEast().lng() || 0;
  const aSouth = map.getBounds()?.getSouthWest().lat() || 0;
  const aWest = map.getBounds()?.getSouthWest().lng() || 0;

  const distanceToTheLeftBottom = getDistance(markerPosition, {
    lat: aSouth,
    lng: aWest,
  });
  const distanceToTheLeftTop = getDistance(markerPosition, {
    lat: aNorth,
    lng: aWest,
  });
  const distanceToTheRightTop = getDistance(markerPosition, {
    lat: aNorth,
    lng: aEast,
  });
  const distanceToTheRightBottom = getDistance(markerPosition, {
    lat: aSouth,
    lng: aEast,
  });
  console.log('bottom left: ' + distanceToTheLeftBottom);
  console.log('top left: ' + distanceToTheLeftTop);
  console.log('top right: ' + distanceToTheRightTop);
  console.log('bottom right: ' + distanceToTheRightBottom);

  const minDistance = Math.min(
    distanceToTheLeftBottom,
    distanceToTheLeftTop,
    distanceToTheRightTop,
    distanceToTheRightBottom
  );
  const sumOfDistances =
    distanceToTheLeftBottom +
    distanceToTheLeftTop +
    distanceToTheRightTop +
    distanceToTheRightBottom;

  if (minDistance === distanceToTheLeftBottom) {
    if (distanceToTheLeftBottom / sumOfDistances < 0.1) {
      return windowOnTopRight;
    }
    if (distanceToTheLeftTop > distanceToTheRightBottom) {
      return windowOnTop;
    } else {
      return windowOnRight;
    }
  }
  if (minDistance === distanceToTheLeftTop) {
    if (distanceToTheLeftTop / sumOfDistances < 0.1) {
      return windowOnBottomRight;
    }
    if (distanceToTheLeftBottom > distanceToTheRightTop) {
      return windowOnBottom;
    } else {
      return windowOnRight;
    }
  }
  if (minDistance === distanceToTheRightTop) {
    if (distanceToTheRightTop / sumOfDistances < 0.1) {
      return windowOnBottomLeft;
    }
    if (distanceToTheRightBottom > distanceToTheLeftTop) {
      return windowOnBottom;
    } else {
      return windowOnLeft;
    }
  }
  if (minDistance === distanceToTheRightBottom) {
    if (distanceToTheRightBottom / sumOfDistances < 0.1) {
      return windowOnTopLeft;
    }
    if (distanceToTheLeftBottom > distanceToTheRightTop) {
      return windowOnLeft;
    } else {
      return windowOnTop;
    }
  }
  return windowOnTop;
};

const buildListingMarker = (props: MarkerType) => {
  const { position, type, isActive, price, optimized } = props;

  let markerIcon: MarkerIcon,
    markerLabel: MarkerLabel | null = null;

  if (price) {
    markerLabel = {
      text: formatPrice(price),
      color: isActive ? 'white' : 'white',
      className: clsx([styles.marker]),
    };
  }

  if (type === 'attraction') {
    markerIcon = {
      url: '/icons/attraction-pin.svg',
    };
  } else if (type === 'property') {
    markerIcon = {
      url: '/icons/property-pin.svg',
    };
  } else {
    markerIcon = {
      url: '/icons/black-marker.svg',
      scaledSize: new google.maps.Size(100, 27),
    };
  }

  return {
    position,
    icon: markerIcon,
    label: markerLabel,
    optimized,
  };
};

const formatPrice = (price?: number) => {
  if (!price) return 'NA';
  if (price >= 1000000) {
    return `${splitByCommas(toDollars(price / 1000000, 2))}M`;
  }
  if (price >= 1000) {
    return `${splitByCommas(toDollars(price / 1000, 0))}k`;
  }
  return `${splitByCommas(toDollars(price, 0))}`;
};

const prepareListingMarkerInfo = (
  {
    price_for_selected_dates,
    sixty_days_average_price,
    address,
    id,
  }: FeaturedListingType,
  listingHref: any,
  dates?: DatesRange,
  markerListingInfo?: FeaturedListingType
) => ({
  position: { lng: address.coords.lon, lat: address.coords.lat },
  price:
    !!dates && dates.from && dates.to
      ? calculateMonthlyPrice({ dates, price_for_selected_dates })
      : sixty_days_average_price,
  optimized: false,
  isActive: id === markerListingInfo?.id,
  id,
  infoWindowContent: markerListingInfo
    ? renderListingInfoWindow(markerListingInfo, listingHref)
    : '',
  listingInfo: markerListingInfo,
});

const renderListingInfo = (
  listing: FeaturedListingType,
  listingHref: any,
  customImage?: JSX.Element
) => {
  const {
    title,
    address: { city = '' },
    bedrooms_total,
    bathrooms,
  } = listing;

  return (
    <div id="info-window">
      <div className="upper-block">
        <div className="upper-block-image-wrapper">
          {customImage || (
            <div className="listing-image" id="listing-carousel" />
          )}
        </div>

        <Link href={listingHref}>
          <a className="upper-block-info" target="_blank" rel="noreferrer">
            <div className="listing-title">{title}</div>
            <div className="listing-address">{city}</div>
          </a>
        </Link>
      </div>

      <Link href={listingHref}>
        <a className="bottom-block" target="_blank" rel="noreferrer">
          <div className="listing-feature">
            <div>Bedrooms</div>
            <div>{bedrooms_total}</div>
          </div>

          <div className="listing-feature">
            <div>Bathrooms</div>
            <div>{bathrooms}</div>
          </div>

          <div className="listing-feature" id="map-listing-price">
            <div />
            <div />
          </div>
        </a>
      </Link>
    </div>
  );
};

const renderMobileListingPopup = (
  listing: FeaturedListingType,
  onClose: () => void,
  listingHref: any
) => (
  <div className={styles.mobileListingPopup}>
    <Cross
      className={styles.closeButton}
      onClick={onClose}
      stroke={colorsByTheme['white'].textPrimaryColor}
    />

    {renderListingInfo(
      listing,
      listingHref,
      <ImageWithLoader
        containerStyle={styles.listingImage}
        src={listing.photos[0]}
        alt="Listing image"
        sizes="50vw"
        priority
      />
    )}
  </div>
);

const renderListingInfoWindow = (
  props: FeaturedListingType,
  listingHref: any
) => {
  return ReactDOMServer.renderToString(renderListingInfo(props, listingHref));
};

/** bottom_left_lat,bottom_left_lon,top_right_lat,top_right_lon */
const getBoundsStringFromMap = (map: google.maps.Map) => {
  return `${map.getBounds()?.getSouthWest().lat()},${map
    .getBounds()
    ?.getSouthWest()
    .lng()},${map.getBounds()?.getNorthEast().lat()},${map
    .getBounds()
    ?.getNorthEast()
    .lng()}`;
};

export {
  buildListingMarker,
  prepareListingMarkerInfo,
  getBoundsStringFromMap,
  getDistance,
  getInfoWindowPosition,
  renderMobileListingPopup,
};
